import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  Globe,
  Instagram,
  Facebook,
  Mail,
  Phone,
  UtensilsCrossed,
  Handshake,
  Download,
} from "lucide-react";
import { ASSETS, BRAND } from "../lib/data";

const TikTokIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const LINKS = [
  {
    id: "website",
    label: "Visit Our Website",
    sub: "monsieurtoast.com",
    icon: Globe,
    href: "https://monsieurtoast.com",
    external: true,
  },
  {
    id: "menu",
    label: "Discover Our Menu",
    sub: "Signature Toasts",
    icon: UtensilsCrossed,
    href: "https://monsieurtoast.com/#menu",
    external: true,
  },
  {
    id: "instagram",
    label: "Instagram",
    sub: "@monsieurtoastofficial",
    icon: Instagram,
    href: "https://www.instagram.com/monsieurtoastofficial",
    external: true,
  },
  {
    id: "tiktok",
    label: "TikTok",
    sub: "@monsieur.toast",
    icon: TikTokIcon,
    href: "https://www.tiktok.com/@monsieur.toast",
    external: true,
  },
  {
    id: "facebook",
    label: "Facebook",
    sub: "@MonsieurToast",
    icon: Facebook,
    href: "https://facebook.com/MonsieurToast",
    external: true,
  },
  {
    id: "email",
    label: "Email Us",
    sub: BRAND.email,
    icon: Mail,
    href: `mailto:${BRAND.email}`,
    external: false,
  },
  {
    id: "call-ch",
    label: "Call Switzerland",
    sub: BRAND.phoneCH,
    icon: Phone,
    href: BRAND.phoneCHHref,
    external: false,
  },
  {
    id: "call-uae",
    label: "Call Dubai",
    sub: BRAND.phone,
    icon: Phone,
    href: BRAND.phoneHref,
    external: false,
  },
  {
    id: "partnerships",
    label: "Partnerships & Franchising",
    sub: "Let's talk",
    icon: Handshake,
    href: `mailto:${BRAND.email}?subject=Partnership%20%26%20Franchising%20enquiry`,
    external: false,
  },
  {
    id: "vcard",
    label: "Save Contact",
    sub: "vCard download",
    icon: Download,
    href: "/monsieur-toast.vcf",
    external: false,
    download: "monsieur-toast.vcf",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function LinksPage() {
  return (
    <>
      <Helmet>
        <title>Monsieur Toast — Links</title>
        <meta
          name="description"
          content="Monsieur Toast — French Toasted Sandwiches. All our links in one place: menu, Instagram, TikTok, Facebook, contact, and more. Croque la vie !"
        />
        <meta name="theme-color" content="#000000" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Monsieur Toast — Links" />
        <meta
          property="og:description"
          content="French Toasted Sandwiches — Croque la vie ! All our links in one place."
        />
        <meta property="og:url" content="https://monsieurtoast.com/links" />
        <meta property="og:image" content="https://monsieurtoast.com/feature-toast.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Monsieur Toast — Links" />
        <meta
          name="twitter:description"
          content="French Toasted Sandwiches — Croque la vie !"
        />
        <meta name="twitter:image" content="https://monsieurtoast.com/feature-toast.jpg" />
        <link rel="canonical" href="https://monsieurtoast.com/links" />
      </Helmet>

      <main
        data-testid="links-page"
        className="min-h-screen bg-black text-white flex flex-col items-center px-5 py-10 md:py-16"
      >
        <div className="grain" />

        {/* Logo + tagline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md flex flex-col items-center text-center"
        >
          <img
            src={ASSETS.logoGold}
            alt="Monsieur Toast"
            data-testid="links-logo"
            className="h-56 sm:h-64 w-auto select-none -my-10"
            draggable="false"
          />
          <h1
            data-testid="links-title"
            className="mt-2 font-display text-3xl sm:text-4xl tracking-tight text-white"
          >
            MONSIEUR <span className="italic text-gold">TOAST</span>
          </h1>
          <p className="mt-3 text-[10px] sm:text-[11px] tracking-[0.4em] uppercase text-gold/80">
            French Toasted Sandwiches
          </p>
          <p className="mt-3 font-display italic text-xl sm:text-2xl text-white/80">
            Croque la vie !
          </p>

          <div className="mt-6 flex items-center gap-3 text-gold/70">
            <span className="h-px w-10 bg-gold/40" />
            <span className="text-[9px] tracking-[0.4em] uppercase">
              Our Links
            </span>
            <span className="h-px w-10 bg-gold/40" />
          </div>
        </motion.div>

        {/* Buttons */}
        <ul
          data-testid="links-list"
          className="w-full max-w-md mt-10 flex flex-col gap-3"
        >
          {LINKS.map((link, i) => {
            const Icon = link.icon;
            const commonClass =
              "group relative w-full flex items-center gap-4 border border-gold/30 bg-black/40 hover:bg-gold hover:text-black transition-all duration-300 px-5 py-4 text-left";
            const rel = link.external ? "noopener noreferrer" : undefined;
            const target = link.external ? "_blank" : undefined;
            return (
              <motion.li
                key={link.id}
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={i}
              >
                <a
                  data-testid={`links-btn-${link.id}`}
                  href={link.href}
                  rel={rel}
                  target={target}
                  download={link.download}
                  aria-label={link.label}
                  className={commonClass}
                >
                  <span className="flex items-center justify-center w-10 h-10 border border-gold/40 text-gold group-hover:border-black group-hover:text-black transition-colors shrink-0">
                    <Icon size={18} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-display text-base sm:text-lg tracking-tight">
                      {link.label}
                    </span>
                    <span className="block text-[11px] sm:text-xs text-white/55 group-hover:text-black/70 transition-colors truncate">
                      {link.sub}
                    </span>
                  </span>
                  <span
                    aria-hidden="true"
                    className="text-gold/60 group-hover:text-black transition-colors text-lg leading-none"
                  >
                    →
                  </span>
                </a>
              </motion.li>
            );
          })}
        </ul>

        {/* QR — scan to share this card */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 flex flex-col items-center"
        >
          <div className="p-2.5 border border-gold/30">
            <img
              src="/qr/monsieur-toast-qr.png"
              alt="Monsieur Toast — QR code to share this card"
              data-testid="links-qr"
              className="w-32 h-32 sm:w-36 sm:h-36 select-none"
              draggable="false"
            />
          </div>
          <p className="mt-3 text-[9px] tracking-[0.4em] uppercase text-gold/60">
            Scan to share this card
          </p>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="w-full max-w-md mt-12 pt-8 border-t border-gold/15 text-center"
        >
          <p className="text-[10px] tracking-[0.4em] uppercase text-gold/60">
            Life is better toasted.
          </p>
          <p className="mt-3 text-[11px] text-white/40 font-light">
            © {new Date().getFullYear()} Monsieur Toast. All rights reserved.
          </p>
        </motion.div>
      </main>
    </>
  );
}
